import React, { Component } from 'react';
import { satisfies, validate } from 'compare-versions';
import { Progress } from 'semantic-ui-react';

import { HTTP_OPTIONS, NODE_TYPE, ROLES } from '@silverfort-nodelibs/common/utils/consts';
import Operation from './Operation';
import NodeCard from './NodeCard';
import ConfirmationMessage from '../Controls/ConfirmationMessage';
import { httpGet, httpPost, isUserPermitted, removeTrailingTags, updateUserToState } from '../utils';
import AutoScroll from '../Controls/AutoScroll/AutoScroll';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';
import { isNewerVersion } from '@silverfort-nodelibs/common/utils/common';
import promptWarningIcon from '../common/Images/triangle_warning.svg';
import NotificationBanner from '../Banners/NotificationBanner';

let statusInterval = null;

class SoftwareUpdates extends Component {
    constructor(props) {
        super(props);

        this.state = {
            systemMode: props.systemMode,
            loading: true,
            checkingVersion: false,
            autoADAdapterUpgrade: true,
            allowAzureOpenAiUsage: false,
            checkingConnectivity: false,
            currentTowerVersion: null,
            currentCastleVersion: null,
            maxTowerVersion: null,
            lastDownloadedVersion: null,
            downloading: false,
            downloadingProgress: 0,
            downloadError: '',
            upgradingTower: false,
            towerUpgradeProgress: 0,
            towerUpgradeDescription: '',
            towerUpgradeError: '',
            upgradingNodes: false,
            nodesUpgradeProgress: 0,
            nodesUpgradeDescription: '',
            nodesUpgradeError: '',
            upgradingPushServices: false,
            pushServicesUpgradeProgress: 0,
            pushServicesUpgradeDescription: '',
            pushServicesUpgradeError: '',
            upgradingDcs: false,
            dcsUpgradeProgress: 0,
            dcsUpgradeDescription: '',
            dcsUpgradeError: '',
            nodes: [],
            pushServices: [],
            dcs: [],
            companyLogo: 'company_logo.png',
            showUpdateTowerConfirmationMessage: false,
            showUpdateNodesConfirmationMessage: false,
            showUpdateDcsConfirmationMessage: false,
            showUpdatePushServicesConfirmationMessage: false,
            selectedNodes: {},
            selectedDcs: {},
            selectedPushServices: {},
            selectedCount: 0,
            selectedDcsCount: 0,
            selectedPushServicesCount: 0,
            updatableNodesCount: 0,
            updatableDcsCount: 0,
            updatablePushServicesCount: 0,
            showLogs: false,
            logsData: [],
            showUnprotected: false
        };
    }

    componentDidMount() {
        updateUserToState(this, () => this.setState({ loading: false }));
        this.checkForUpdatesHandler();
        this.checkConnectivityHandler();
    }

    clearInterval(interval) {
        if (interval) {
            clearInterval(interval);
        }
    }

    componentWillUnmount() {
        this.clearInterval(statusInterval);
    }

    updateStateFromStatus(status) {
        this.setState({
            statusUpdated: true,
            companyName: status.companyName,
            companyLogo: status.companyLogo,
            autoADAdapterUpgrade: status.autoADAdapterUpgrade,
            allowAzureOpenAiUsage: status.allowAzureOpenAiUsage,
            downloadingVersion: status.downloading,
            currentTowerVersion: status.currentTowerVersion,
            currentCastleVersion: status.currentCastleVersion,
            maxTowerVersion: status.maxTowerVersion,
            lastDownloadedVersion: status.lastDownloadedVersion,
            downloading: status.downloading,
            downloadError: status.downloadError,
            downloadingProgress: status.downloadingProgress,
            upgradingTower: status.upgradingTower,
            towerUpgradeProgress: status.towerUpgradeProgress,
            towerUpgradeDescription: status.towerUpgradeDescription,
            towerUpgradeError: status.towerUpgradeError,
            upgradingNodes: status.upgradingNodes,
            nodesUpgradeProgress: status.nodesUpgradeProgress,
            nodesUpgradeDescription: status.nodesUpgradeDescription,
            nodesUpgradeError: status.nodesUpgradeError,
            upgradingDcs: status.upgradingDcs,
            dcsUpgradeProgress: status.dcsUpgradeProgress,
            dcsUpgradeDescription: status.dcsUpgradeDescription,
            dcsUpgradeError: status.dcsUpgradeError,
            upgradingPushServices: status.upgradingPushServices,
            pushServicesUpgradeProgress: status.pushServicesUpgradeProgress,
            pushServicesUpgradeDescription: status.pushServicesUpgradeDescription,
            pushServicesUpgradeError: status.pushServicesUpgradeError,
            showUnprotected: status.showUnprotected,
            logsData: status.logsData,
            showLogs: !(
                status.towerUpgradeProgress ||
                status.nodesUpgradeProgress ||
                status.pushServicesUpgradeProgress ||
                status.dcsUpgradeProgress ||
                status.towerUpgradeError !== '' ||
                status.nodesUpgradeError !== '' ||
                status.pushServicesUpgradeError !== '' ||
                status.dcsUpgradeError !== ''
            )
                ? false
                : this.state.showLogs
        });
    }

    updateNodesStateFromRes(resData, initial) {
        const nodes = resData.nodes.filter((n) => n.type === NODE_TYPE.WALL);
        const adConnects = resData.nodes.filter((n) => n.type === NODE_TYPE.AD_CONNECT);
        const pushServices = resData.nodes.filter((n) => n.type === NODE_TYPE.ARYA);
        const currentTowerPlatform = resData.currentTowerPlatform;
        const oracleRequiredVersions = resData.oracleRequiredVersions;
        const dcs = resData.nodes.filter((n) => n.type === NODE_TYPE.DC);
        const { selectedNodes, selectedCount, updatableNodesCount } = this.buildSelectedNodesAndCount(
            nodes,
            resData.currentTowerVersion,
            initial,
            NODE_TYPE.WALL
        );
        const castleVersion = resData.currentCastleVersion;
        const {
            selectedNodes: selectedDcs,
            selectedCount: selectedDcsCount,
            updatableNodesCount: updatableDcsCount
        } = this.buildSelectedNodesAndCount(dcs, castleVersion, initial, NODE_TYPE.DC);
        const {
            selectedNodes: selectedPushServices,
            selectedCount: selectedPushServicesCount,
            updatableNodesCount: updatablePushServicesCount
        } = this.buildSelectedNodesAndCount(pushServices, resData.currentTowerVersion, initial, NODE_TYPE.ARYA);

        this.setState({
            nodes,
            adConnects,
            pushServices,
            dcs,
            selectedNodes,
            selectedCount,
            updatableNodesCount,
            selectedDcs,
            selectedDcsCount,
            updatableDcsCount,
            selectedPushServices,
            selectedPushServicesCount,
            updatablePushServicesCount,
            currentTowerPlatform,
            oracleRequiredVersions,
            timeToForceDcsUpgrade: resData.timeToForceDcsUpgrade
        });
    }

    checkStatus = () => {
        httpGet('/softwareUpdates/status', { [HTTP_OPTIONS.HANDLE_ERRORS]: true }).then((res) => {
            if (res && res.data) {
                if (res.data.redirect) {
                    window.location = res.data.redirect;
                }
                this.updateStateFromStatus(res.data);
            }
        });
        if (!this.state.checkingConnectivity) {
            httpGet('/softwareUpdates/nodeVersions', { [HTTP_OPTIONS.HANDLE_ERRORS]: true }).then((res) => {
                if (res && res.data) {
                    if (res.data.redirect) {
                        window.location = res.data.redirect;
                    }
                    const newNodes = [...this.state.nodes, ...this.state.pushServices, ...this.state.dcs];
                    for (const node of res.data.nodes) {
                        const newNode = newNodes.find((n) => n.id === node.id);
                        if (newNode) {
                            newNode.version = node.version;
                            newNode.connected = node.connected;
                            newNode.inventoryName = node.inventoryName;
                        } else {
                            newNodes.push(node);
                        }
                    }
                    res.data.nodes = newNodes;
                    this.updateNodesStateFromRes(res.data, false);
                }
            });
        }
    };

    checkForUpdatesHandler = () => {
        if (this.state.checkingVersion) {
            return;
        }
        this.setState({ checkingVersion: true });
        httpGet('/softwareUpdates/check')
            .then((res) => {
                if (res && res.data) {
                    this.updateStateFromStatus(res.data);
                }
            })
            .catch((err) => {
                console.error(err);
            })
            .finally(() => {
                statusInterval = setInterval(() => this.checkStatus(), 2000);
                this.setState({ checkingVersion: false });
            });
    };

    checkConnectivityHandler = async () => {
        if (this.state.checkingConnectivity) {
            return;
        }
        try {
            this.setState({ checkingConnectivity: true });
            const nodesRes = await httpGet('/softwareUpdates/nodeVersions');
            this.updateNodesStateFromRes(nodesRes.data, true);

            if (this.state.systemMode && this.state.systemMode === SYSTEM_MODES.ON_PREM) {
                const checkConnectivityRes = await httpGet('/softwareUpdates/checkNodesConnectivity');
                this.updateNodesStateFromRes(checkConnectivityRes.data, true);
            }
        } catch (err) {
            console.error(err);
        } finally {
            this.setState({ checkingConnectivity: false });
        }
    };

    downloadNewVersionHandler = () => {
        this.setState({ downloading: true, downloadError: '', downloadingProgress: 0 });
        httpPost(`/softwareUpdates/downloadLatest`).catch((err) => {
            console.error(err);
            this.setState({ downloading: false });
        });
    };

    showUpdateTowerHandler = () => {
        this.setState({ showUpdateTowerConfirmationMessage: true });
    };

    updateTowerHandler = () => {
        this.setState({ showUpdateTowerConfirmationMessage: false, upgradingTower: true, towerUpgradeError: '' });
        httpPost(`/softwareUpdates/upgradeTower`).catch((err) => {
            console.error(err);
            this.setState({ upgradingTower: false });
        });
    };

    showUpdatePushServicesHandler = () => {
        this.setState({ showUpdatePushServicesConfirmationMessage: true });
    };

    updatePushServiceHandler = () => {
        this.setState({
            showUpdatePushServicesConfirmationMessage: false,
            upgradingPushServices: true,
            pushServicesUpgradeError: ''
        });
        const selectedPushServices = [];
        let isAllPushServices = true;

        for (const pushServiceId in this.state.selectedPushServices) {
            if (this.state.selectedPushServices[pushServiceId].selected) {
                selectedPushServices.push(this.state.selectedPushServices[pushServiceId].inventoryName);
            }
        }

        if (selectedPushServices.length < this.state.pushServices.length) {
            isAllPushServices = false;
        }

        httpPost(`/softwareUpdates/upgradePushServices`, { selectedPushServices, isAllPushServices }).catch((err) => {
            console.error(err);
            this.setState({ upgradingPushServices: false });
        });
    };

    showUpdateNodesHandler = () => {
        this.setState({ showUpdateNodesConfirmationMessage: true });
    };

    updateNodesHandler = () => {
        this.setState({ showUpdateNodesConfirmationMessage: false, upgradingNodes: true, nodesUpgradeError: '' });
        const selectedNodes = [];
        let isAllNodes = true;

        for (const nodeId in this.state.selectedNodes) {
            if (this.state.selectedNodes[nodeId].selected) {
                selectedNodes.push(this.state.selectedNodes[nodeId].inventoryName);
            }
        }

        if (selectedNodes.length < this.state.nodes.length) {
            isAllNodes = false;
        }

        httpPost(`/softwareUpdates/upgradeNodes`, { selectedNodes, isAllNodes }).catch((err) => {
            console.error(err);
            this.setState({ upgradingNodes: false });
        });
    };

    showUpdateDcsHandler = () => {
        this.setState({ showUpdateDcsConfirmationMessage: true });
    };

    updateDcsHandler = () => {
        this.setState({ showUpdateDcsConfirmationMessage: false, upgradingDcs: true, dcsUpgradeError: '' });
        const selectedDcs = [];
        let isAllDcs = true;

        for (const dcId in this.state.selectedDcs) {
            if (this.state.selectedDcs[dcId].selected) {
                selectedDcs.push(this.state.selectedDcs[dcId].inventoryName);
            }
        }

        if (selectedDcs.length < this.state.dcs.length) {
            isAllDcs = false;
        }

        httpPost(`/softwareUpdates/upgradeDcs`, { selectedNodes: selectedDcs, isAllNodes: isAllDcs }).catch((err) => {
            console.error(err);
            this.setState({ upgradingDcs: false });
        });
    };

    showUpgradeLogs = () => {
        this.setState({
            showLogs: !this.state.showLogs
        });
    };

    cancelConfirmationHandler = () => {
        this.setState({
            showUpdateNodesConfirmationMessage: false,
            showUpdateTowerConfirmationMessage: false,
            showUpdateDcsConfirmationMessage: false,
            showUpdatePushServicesConfirmationMessage: false
        });
    };

    checkNodeHandler = (nodeId, type) => {
        if (type === NODE_TYPE.WALL) {
            const newValue = !this.state.selectedNodes[nodeId].selected;
            const updatedNode = {
                ...this.state.selectedNodes[nodeId],
                selected: newValue
            };
            this.setState({
                selectedNodes: { ...this.state.selectedNodes, [nodeId]: updatedNode },
                selectedCount: newValue ? this.state.selectedCount + 1 : this.state.selectedCount - 1
            });
        } else if (type === NODE_TYPE.DC) {
            const newValue = !this.state.selectedDcs[nodeId].selected;
            const updatedNode = {
                ...this.state.selectedDcs[nodeId],
                selected: newValue
            };
            this.setState({
                selectedDcs: { ...this.state.selectedDcs, [nodeId]: updatedNode },
                selectedDcsCount: newValue ? this.state.selectedDcsCount + 1 : this.state.selectedDcsCount - 1
            });
        } else if (type === NODE_TYPE.ARYA) {
            const newValue = !this.state.selectedPushServices[nodeId].selected;
            const updatedPushService = {
                ...this.state.selectedPushServices[nodeId],
                selected: newValue
            };
            this.setState({
                selectedPushServices: { ...this.state.selectedPushServices, [nodeId]: updatedPushService },
                selectedPushServicesCount: newValue
                    ? this.state.selectedPushServicesCount + 1
                    : this.state.selectedPushServicesCount - 1
            });
        }
    };

    selectAllHandler = (select, type) => {
        if (type === NODE_TYPE.WALL) {
            const newSelectedNodes = { ...this.state.selectedNodes };
            let newSelectedCount = 0;
            for (const key in newSelectedNodes) {
                if (select && newSelectedNodes[key].updateable) {
                    newSelectedNodes[key].selected = true;
                    newSelectedCount++;
                } else {
                    newSelectedNodes[key].selected = false;
                }
            }
            this.setState({ selectedNodes: newSelectedNodes, selectedCount: newSelectedCount });
        } else if (type === NODE_TYPE.DC) {
            const newSelectedDcs = { ...this.state.selectedDcs };
            let newSelectedCount = 0;
            for (const key in newSelectedDcs) {
                if (select && newSelectedDcs[key].updateable) {
                    newSelectedDcs[key].selected = true;
                    newSelectedCount++;
                } else {
                    newSelectedDcs[key].selected = false;
                }
            }
            this.setState({ selectedDcs: newSelectedDcs, selectedDcsCount: newSelectedCount });
        } else if (type === NODE_TYPE.ARYA) {
            const newSelectedPushServices = { ...this.state.selectedPushServices };
            let newSelectedCount = 0;
            for (const key in newSelectedPushServices) {
                if (select && newSelectedPushServices[key].updateable) {
                    newSelectedPushServices[key].selected = true;
                    newSelectedCount++;
                } else {
                    newSelectedPushServices[key].selected = false;
                }
            }
            this.setState({
                selectedPushServices: newSelectedPushServices,
                selectedPushServicesCount: newSelectedCount
            });
        }
    };

    buildSelectedNodesAndCount(nodes, currentVersion, initial, type) {
        const nodesData = {
            [NODE_TYPE.WALL]: {
                selectedNodes: this.state.selectedNodes,
                selectedCount: this.state.selectedCount,
                updatableNodesCount: this.state.updatableNodesCount
            },
            [NODE_TYPE.DC]: {
                selectedNodes: this.state.selectedDcs,
                selectedCount: this.state.selectedDcsCount,
                updatableNodesCount: this.state.updatableDcsCount
            },
            [NODE_TYPE.ARYA]: {
                selectedNodes: this.state.selectedPushServices,
                selectedCount: this.state.selectedPushServicesCount,
                updatableNodesCount: this.state.updatablePushServicesCount
            }
        };
        if (!nodes) {
            return nodesData[type];
        }

        const selectedNodes = { ...nodesData[type].selectedNodes };
        let updatableNodesCount = 0;
        for (const node of nodes) {
            if (!this.nodeCanUpdate(currentVersion, node)) {
                selectedNodes[node.id] = {
                    selected: false,
                    updateable: false,
                    inventoryName: node.inventoryName,
                    type: node.type
                };
            } else {
                updatableNodesCount++;

                if (initial) {
                    selectedNodes[node.id] = {
                        selected: true
                    };
                }

                if (selectedNodes[node.id]) {
                    selectedNodes[node.id].updateable = true;
                    selectedNodes[node.id].inventoryName = node.inventoryName;
                }
            }
        }

        let selectedCount = 0;
        for (const selectedNode of Object.values(selectedNodes)) {
            if (selectedNode.selected) {
                selectedCount++;
            }
        }

        return { selectedNodes, selectedCount, updatableNodesCount };
    }

    nodeCanUpdate(currentVersion, node) {
        return node.connected && node.inventoryName && this.nodeNeedUpdate(currentVersion, node);
    }

    nodeNeedUpdate(currentVersion, node) {
        if (!currentVersion) {
            return false;
        }
        let nodeVersion = node.version;
        if (nodeVersion && nodeVersion.toLowerCase().endsWith('-rc')) {
            nodeVersion = nodeVersion.substring(0, nodeVersion.length - 3);
        }
        return !nodeVersion || nodeVersion.toLowerCase() !== currentVersion.toLowerCase();
    }

    renderNodeCards(nodes, updateNodesDisabled, type) {
        return (
            nodes &&
            nodes
                .sort((n1, n2) => n1.name.localeCompare(n2.name))
                .map((node) => {
                    const selectedNodes = {
                        [NODE_TYPE.WALL]: this.state.selectedNodes,
                        [NODE_TYPE.DC]: this.state.selectedDcs,
                        [NODE_TYPE.ARYA]: this.state.selectedPushServices,
                        [NODE_TYPE.AD_CONNECT]: []
                    };
                    const selectedNode = selectedNodes[type][node.id];
                    return (
                        <NodeCard
                            hideCheckbox={node.type === NODE_TYPE.AD_CONNECT}
                            key={node.id}
                            id={node.id}
                            type={type}
                            canUpdate={!updateNodesDisabled && selectedNode ? selectedNode.updateable : false}
                            checkingConnectivity={this.state.checkingConnectivity}
                            connected={node.connected}
                            nodeChecked={this.checkNodeHandler}
                            nodeCheckValue={selectedNode ? selectedNode.selected : false}
                            headerDescription={node.name}
                            headerAZ={node.az}
                            footerHeader={node.version ? removeTrailingTags(node.version) : 'N/A'}
                            connectivityReason={node.connectivityReason}
                            domain={node.domain}
                        />
                    );
                })
        );
    }

    changeShowUnprotectedState() {
        this.setState({
            showUnprotected: !this.state.showUnprotected
        });

        httpPost(
            `/softwareUpdates/changeShowUnprotectedState`,
            {
                showUnprotected: !this.state.showUnprotected
            },
            { [HTTP_OPTIONS.HANDLE_ERRORS]: true }
        );
    }

    isAllowedToUpgradeDcs(walls) {
        const castleVersion = removeTrailingTags(this.state.currentCastleVersion);
        if (!validate(castleVersion)) {
            return false;
        }
        const oneNodeInSameMajorCastleVersion = this.state.nodes.some((node) => {
            return (
                validate(node.version) &&
                (satisfies(node.version, `~${castleVersion}`) || satisfies(castleVersion, `~${node.version}`)) // (major.minor.patch) Checks if castle and wall have same major and minor version.
            );
        });
        return !this.state.autoADAdapterUpgrade && oneNodeInSameMajorCastleVersion;
    }

    render() {
        const { isCloudOnly } = this.props;
        const {
            currentTowerVersion,
            currentCastleVersion,
            lastDownloadedVersion,
            maxTowerVersion,
            nodes,
            pushServices,
            dcs,
            adConnects,
            logsData
        } = this.state;
        const isEditor = isUserPermitted(this.state.user, 'softwareUpdates', ROLES.EDITOR.value);

        let downloadVersionDesc = 'You have the latest version';
        let downloadVersionDescExtra = currentTowerVersion ? removeTrailingTags(currentTowerVersion) : '';
        let downloadErrorText = '';
        let downloadDisabled = true;
        let upgradeLogsText = '';
        if (this.state.checkingVersion) {
            downloadVersionDesc = 'Checking for new version...';
            downloadVersionDescExtra = '';
        } else if (this.state.downloading) {
            downloadVersionDesc = 'Downloading new version...';
            downloadVersionDescExtra = 'This may take a while';
        } else if (maxTowerVersion && isNewerVersion(maxTowerVersion, currentTowerVersion)) {
            downloadVersionDescExtra = maxTowerVersion ? removeTrailingTags(maxTowerVersion) : maxTowerVersion;
            if (lastDownloadedVersion === maxTowerVersion) {
                downloadVersionDesc = 'New version ready for update';
            } else {
                downloadVersionDesc = 'New version available';
                downloadDisabled = false;
            }
        }
        if (this.state.downloadError !== '') {
            downloadErrorText = this.state.downloadError;
        }

        let updateTowerDesc = 'Admin Console';
        let updateTowerDescExtra = 'You have the latest version';
        let updateTowerDisabled = true;
        if (this.state.upgradingTower) {
            updateTowerDesc = 'Updating in progress...';
            updateTowerDescExtra = 'This may take a while';
            upgradeLogsText = 'Updating Admin Console (' + this.state.towerUpgradeProgress.toFixed(0) + '%)';
        } else if (maxTowerVersion && isNewerVersion(maxTowerVersion, currentTowerVersion)) {
            updateTowerDescExtra = `Ready for update to version: ${
                lastDownloadedVersion ? removeTrailingTags(lastDownloadedVersion) : ''
            }`;
            if (lastDownloadedVersion !== maxTowerVersion) {
                updateTowerDescExtra = 'Download latest version first';
            } else {
                updateTowerDisabled = false;
            }
        }

        let updatePushServicesDesc = 'Desktop Messaging Services';
        let updatePushServicesDescExtra = 'You have the latest version';
        let updatePushServicesDisabled = true;
        let pushServicesNeedUpdate = true;
        if (this.state.upgradingPushServices) {
            updatePushServicesDesc = 'Updating in progress...';
            updatePushServicesDescExtra = 'This may take a while';
            upgradeLogsText =
                'Updating Desktop Messaging Services (' + this.state.pushServicesUpgradeProgress.toFixed(0) + '%)';
        } else if (pushServices && pushServices.some((node) => this.nodeNeedUpdate(currentTowerVersion, node))) {
            if (this.state.downloading) {
                updatePushServicesDescExtra = 'Downloading is in progress';
            } else if (currentTowerVersion !== lastDownloadedVersion) {
                updatePushServicesDescExtra = 'Update Admin Console first';
            } else {
                updatePushServicesDescExtra = `Ready for update to version: ${
                    currentTowerVersion ? removeTrailingTags(currentTowerVersion) : ''
                }`;
                updatePushServicesDisabled = false;
            }
        } else {
            pushServicesNeedUpdate = false;
        }

        let updateNodesDesc = 'Silverfort Nodes';
        let updateNodesDescExtra = 'You have the latest version';
        let updateNodesDisabled = true;
        if (this.state.upgradingNodes) {
            updateNodesDesc = 'Updating in progress...';
            updateNodesDescExtra = 'This may take a while';
            upgradeLogsText = 'Updating Silverfort Nodes (' + this.state.nodesUpgradeProgress.toFixed(0) + '%)';
        } else if (nodes && nodes.some((node) => this.nodeNeedUpdate(currentTowerVersion, node))) {
            if (this.state.downloading) {
                updateNodesDescExtra = 'Downloading is in progress';
            } else if (currentTowerVersion !== lastDownloadedVersion) {
                updateNodesDescExtra = 'Update admin console first';
            } else if (pushServicesNeedUpdate) {
                updateNodesDescExtra = 'Update messaging services first';
            } else {
                updateNodesDescExtra = `Ready for update to version: ${
                    currentTowerVersion ? removeTrailingTags(currentTowerVersion) : ''
                }`;
                updateNodesDisabled = false;
            }
        }

        let updateDcsDesc = 'Domain Controllers';
        let updateDcsDescExtra = 'You have the latest version';
        let updateDcsDisabled = true;
        if (this.state.upgradingDcs) {
            updateDcsDesc = 'Updating in progress...';
            updateDcsDescExtra = 'This may take a while';
            upgradeLogsText = 'Updating DCs (' + this.state.nodesUpgradeProgress.toFixed(0) + '%)';
        } else if (dcs && dcs.some((dc) => this.nodeNeedUpdate(currentCastleVersion, dc))) {
            if (this.state.systemMode && this.state.systemMode !== SYSTEM_MODES.ON_PREM) {
                updateDcsDescExtra = `Ready for update to version: ${
                    currentCastleVersion ? removeTrailingTags(currentCastleVersion) : ''
                }`;
                updateDcsDisabled = false;
            } else if (this.state.downloading) {
                updateDcsDescExtra = 'Downloading is in progress';
            } else if (currentTowerVersion !== lastDownloadedVersion) {
                updateDcsDescExtra = 'Update admin console first';
            } else if (!this.isAllowedToUpgradeDcs()) {
                updateDcsDescExtra = 'Update Nodes first';
            } else {
                updateDcsDescExtra = `Ready for update to version: ${
                    currentCastleVersion ? removeTrailingTags(currentCastleVersion) : ''
                }`;
                updateDcsDisabled = false;
            }
        }

        const wallCards = this.renderNodeCards(nodes, updateNodesDisabled, NODE_TYPE.WALL);
        const aryaCards = this.renderNodeCards(pushServices, updatePushServicesDisabled, NODE_TYPE.ARYA);
        const dcCards = this.renderNodeCards(
            this.state.showUnprotected ? dcs : dcs.filter((dc) => dc.is_protected),
            updateDcsDisabled,
            NODE_TYPE.DC
        );

        const adcCards = this.renderNodeCards(adConnects, false, NODE_TYPE.AD_CONNECT);

        let updatePushServicesButtonDesc = 'Update All Services';
        if (this.state.checkingConnectivity) {
            updatePushServicesButtonDesc = 'Checking connectivity...';
        } else if (this.state.updatablePushServicesCount > 0) {
            if (this.state.selectedPushServicesCount === 0) {
                updatePushServicesButtonDesc = 'No services selected';
            } else if (pushServices && this.state.selectedPushServicesCount !== pushServices.length) {
                updatePushServicesButtonDesc = `Update selected services (${this.state.selectedPushServicesCount})`;
            }
        }

        let updateNodesButtonDesc = 'Update All Nodes';
        if (this.state.checkingConnectivity) {
            updateNodesButtonDesc = 'Checking connectivity...';
        } else if (this.state.updatableNodesCount > 0) {
            if (this.state.selectedCount === 0) {
                updateNodesButtonDesc = 'No nodes selected';
            } else if (nodes && this.state.selectedCount !== nodes.length) {
                updateNodesButtonDesc = `Update selected nodes (${this.state.selectedCount})`;
            }
        }

        let updateDcsButtonDesc = 'Update All DCs';
        if (this.state.checkingConnectivity) {
            updateDcsButtonDesc = 'Checking connectivity...';
        } else if (this.state.updatableDcsCount > 0) {
            if (this.state.selectedDcsCount === 0) {
                updateDcsButtonDesc = 'No DCs selected';
            } else if (dcs && this.state.selectedDcsCount !== dcs.length) {
                updateDcsButtonDesc = `Update selected DCs (${this.state.selectedDcsCount})`;
            }
        }

        const downloadPercentage = this.state.downloadingProgress.toFixed(0);
        const towerUpgradePercentage = this.state.towerUpgradeProgress.toFixed(0);
        const nodesUpgradePercentage = this.state.nodesUpgradeProgress.toFixed(0);
        const dcsUpgradePercentage = this.state.dcsUpgradeProgress.toFixed(0);
        const pushServicesUpgradePercentage = this.state.pushServicesUpgradeProgress.toFixed(0);

        const isUpgrading =
            this.state.upgradingTower ||
            this.state.upgradingNodes ||
            this.state.upgradingDcs ||
            this.state.upgradingPushServices;
        const isUpgradeError =
            this.state.towerUpgradeError !== '' ||
            this.state.nodesUpgradeError !== '' ||
            this.state.pushServicesUpgradeError !== '' ||
            this.state.dcsUpgradeError !== '';

        const logRows = [];
        for (const logData of logsData) {
            logRows.push(
                <div key={logData} className="logRow">
                    {logData}
                </div>
            );
        }

        return (
            <div>
                <div id="updates">
                    {this.state.loading && (
                        <div className="silverfort-loader">
                            <div className="logo"></div>
                        </div>
                    )}
                    <div className="contentContainer">
                        {this.state.systemMode !== SYSTEM_MODES.ON_PREM &&
                            this.state.timeToForceDcsUpgrade &&
                            !this.state.hideDcsUpgradeBanner && (
                                <div className="px-8 pt-4">
                                    <NotificationBanner
                                        theme="warn"
                                        height="48px"
                                        padding="16px"
                                        messagePadding="8px"
                                        content={
                                            <div className="flex justify-between">
                                                <div className="flex gap-2">
                                                    <div className="text-sf-dark-500">
                                                        {`New version available for upgrade until ${new Date(
                                                            this.state.timeToForceDcsUpgrade
                                                        ).toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: 'numeric'
                                                        })}. On that
                                                        date, Domain Controllers will upgrade automatically.`}
                                                    </div>
                                                    <div
                                                        onClick={() => {
                                                            this.setState({ hideDcsUpgradeBanner: true });
                                                            this.showUpdateDcsHandler();
                                                        }}
                                                        className="hover:text-sf-primary-700 text-sf-primary-500 cursor-pointer">
                                                        Upgrade
                                                    </div>
                                                </div>
                                                <div
                                                    onClick={() => this.setState({ hideDcsUpgradeBanner: true })}
                                                    className="hover:text-sf-gray-600 text-sf-light-700 underline decoration-1 cursor-pointer">
                                                    Not Now
                                                </div>
                                            </div>
                                        }
                                    />
                                </div>
                            )}
                        <div className={`content`}>
                            <div className="header">
                                <div className="companyRow">
                                    <div className="companyLogo">
                                        <img src={this.state.companyLogo} alt="" />
                                        <span>{this.state.companyName}</span>
                                    </div>
                                    <div className="towerVersion">
                                        Current Admin Console Version: {removeTrailingTags(currentTowerVersion)}
                                    </div>
                                </div>
                                <div className="operations">
                                    {this.state.systemMode !== SYSTEM_MODES.SAAS && (
                                        <div className="operation-section">
                                            {!this.state.downloading && (
                                                <Operation
                                                    description={downloadVersionDesc}
                                                    descriptionExtra={downloadVersionDescExtra}
                                                    errorText={downloadErrorText}
                                                    buttonClicked={this.downloadNewVersionHandler}
                                                    buttonDisabled={
                                                        !isEditor ||
                                                        this.state.downloading ||
                                                        this.state.upgradingTower ||
                                                        this.state.upgradingNodes ||
                                                        this.state.upgradingPushServices ||
                                                        downloadDisabled
                                                    }
                                                    buttonDescription="Download"
                                                />
                                            )}
                                            {this.state.downloading && (
                                                <>
                                                    <div className="operation-progress-header">
                                                        Downloading...({downloadPercentage}%)
                                                    </div>
                                                    <div className="operation-progress">
                                                        <Progress percent={downloadPercentage} />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                    {this.state.systemMode !== SYSTEM_MODES.SAAS && (
                                        <div className="operation-section">
                                            {!this.state.upgradingTower && (
                                                <Operation
                                                    description={updateTowerDesc}
                                                    descriptionExtra={updateTowerDescExtra}
                                                    errorText={this.state.towerUpgradeError}
                                                    showUpgradeLogs={this.showUpgradeLogs}
                                                    buttonClicked={this.showUpdateTowerHandler}
                                                    buttonDisabled={
                                                        !isEditor ||
                                                        this.state.downloading ||
                                                        !lastDownloadedVersion ||
                                                        updateTowerDisabled
                                                    }
                                                    buttonDescription="Update"
                                                />
                                            )}
                                            {this.state.upgradingTower && (
                                                <>
                                                    <div className="operation-progress-header">
                                                        Updating...({towerUpgradePercentage}%)
                                                    </div>
                                                    <div className="operation-progress-description">
                                                        {this.state.towerUpgradeDescription}
                                                    </div>
                                                    <div className="operation-progress">
                                                        <Progress percent={towerUpgradePercentage} />
                                                    </div>
                                                    <button
                                                        disabled={this.state.towerUpgradeProgress === 0}
                                                        onClick={this.showUpgradeLogs}>
                                                        {this.state.showLogs ? 'Hide Logs' : 'Show Logs'}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                    {this.state.systemMode !== SYSTEM_MODES.SAAS &&
                                        !isCloudOnly &&
                                        pushServices.length > 0 && (
                                            <div className="operation-section">
                                                {!this.state.upgradingPushServices && (
                                                    <Operation
                                                        description={updatePushServicesDesc}
                                                        descriptionExtra={updatePushServicesDescExtra}
                                                        errorText={this.state.pushServicesUpgradeError}
                                                        showUpgradeLogs={this.showUpgradeLogs}
                                                        buttonClicked={this.showUpdatePushServicesHandler}
                                                        buttonDisabled={
                                                            !isEditor ||
                                                            this.state.downloading ||
                                                            this.state.checkingConnectivity ||
                                                            !lastDownloadedVersion ||
                                                            updatePushServicesDisabled ||
                                                            this.state.selectedPushServicesCount === 0
                                                        }
                                                        buttonDescription={updatePushServicesButtonDesc}
                                                    />
                                                )}
                                                {this.state.upgradingPushServices && (
                                                    <>
                                                        <div className="operation-progress-header">
                                                            Updating...({pushServicesUpgradePercentage}%)
                                                        </div>
                                                        <div className="operation-progress-description">
                                                            {this.state.pushServicesUpgradeDescription}
                                                        </div>
                                                        <div className="operation-progress">
                                                            <Progress percent={pushServicesUpgradePercentage} />
                                                        </div>
                                                        <button onClick={this.showUpgradeLogs}>
                                                            {this.state.showLogs ? 'Hide Logs' : 'Show Logs'}
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    {this.state.systemMode !== SYSTEM_MODES.SAAS && !isCloudOnly && (
                                        <div className="operation-section">
                                            {!this.state.upgradingNodes && (
                                                <Operation
                                                    description={updateNodesDesc}
                                                    descriptionExtra={updateNodesDescExtra}
                                                    errorText={this.state.nodesUpgradeError}
                                                    showUpgradeLogs={this.showUpgradeLogs}
                                                    buttonClicked={this.showUpdateNodesHandler}
                                                    buttonDisabled={
                                                        !isEditor ||
                                                        this.state.downloading ||
                                                        this.state.checkingConnectivity ||
                                                        !lastDownloadedVersion ||
                                                        updateNodesDisabled ||
                                                        this.state.selectedCount === 0
                                                    }
                                                    buttonDescription={updateNodesButtonDesc}
                                                />
                                            )}
                                            {this.state.upgradingNodes && (
                                                <>
                                                    <div className="operation-progress-header">
                                                        Updating...({nodesUpgradePercentage}%)
                                                    </div>
                                                    <div className="operation-progress-description">
                                                        {this.state.nodesUpgradeDescription}
                                                    </div>
                                                    <div className="operation-progress">
                                                        <Progress percent={nodesUpgradePercentage} />
                                                    </div>
                                                    <button onClick={this.showUpgradeLogs}>
                                                        {this.state.showLogs ? 'Hide Logs' : 'Show Logs'}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                    {this.state.systemMode !== SYSTEM_MODES.ON_PREM && (
                                        <div className="operation-section">
                                            <div className="operation flex flex-col justify-start">
                                                <div className="description-section">
                                                    <div className="descriptionHeader">{'AD Connect'}</div>
                                                    <div className="description">Updated automatically</div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {(this.state.systemMode !== SYSTEM_MODES.ON_PREM ||
                                        !this.state.autoADAdapterUpgrade) && (
                                        <div className="operation-section">
                                            {!this.state.upgradingDcs && (
                                                <Operation
                                                    description={updateDcsDesc}
                                                    descriptionExtra={updateDcsDescExtra}
                                                    errorText={this.state.dcsUpgradeError}
                                                    showUpgradeLogs={this.showUpgradeLogs}
                                                    buttonClicked={this.showUpdateDcsHandler}
                                                    buttonDisabled={
                                                        !isEditor ||
                                                        this.state.downloading ||
                                                        this.state.checkingConnectivity ||
                                                        (!lastDownloadedVersion &&
                                                            this.state.systemMode === SYSTEM_MODES.ON_PREM) ||
                                                        updateDcsDisabled ||
                                                        this.state.selectedDcsCount === 0
                                                    }
                                                    buttonDescription={updateDcsButtonDesc}
                                                />
                                            )}
                                            {this.state.upgradingDcs && (
                                                <>
                                                    <div className="operation-progress-header">
                                                        Updating...({dcsUpgradePercentage}%)
                                                    </div>
                                                    <div className="operation-progress-description">
                                                        {this.state.dcsUpgradeDescription}
                                                    </div>
                                                    <div className="operation-progress">
                                                        <Progress percent={dcsUpgradePercentage} />
                                                    </div>
                                                    <button onClick={this.showUpgradeLogs}>
                                                        {this.state.showLogs ? 'Hide Logs' : 'Show Logs'}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            {this.state.showLogs && (isUpgrading || isUpgradeError) && (
                                <div>
                                    <div className="logsHeader">{upgradeLogsText}</div>
                                    <AutoScroll className="logs light-scrollbar">{logRows}</AutoScroll>
                                </div>
                            )}
                            {this.state.systemMode !== SYSTEM_MODES.SAAS && !isCloudOnly && pushServices.length > 0 && (
                                <>
                                    <div className="nodesHeaderContainer">
                                        <div className="nodesHeader">
                                            Desktop Messaging Services ({aryaCards ? aryaCards.length : 0})
                                        </div>
                                        {!updatePushServicesDisabled && this.state.updatablePushServicesCount > 0 && (
                                            <>
                                                <div
                                                    className="link"
                                                    onClick={() => this.selectAllHandler(true, NODE_TYPE.ARYA)}>
                                                    Select all
                                                </div>
                                                <div
                                                    className="link"
                                                    onClick={() => this.selectAllHandler(false, NODE_TYPE.ARYA)}>
                                                    Clear selection
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <div className="nodeCards">{aryaCards}</div>
                                </>
                            )}
                            {this.state.systemMode !== SYSTEM_MODES.SAAS && !isCloudOnly && (
                                <>
                                    <div className="nodesHeaderContainer">
                                        <div className="nodesHeader">
                                            Silverfort Nodes ({wallCards ? wallCards.length : 0})
                                        </div>
                                        {!updateNodesDisabled && this.state.updatableNodesCount > 0 && (
                                            <>
                                                <div
                                                    className="link"
                                                    onClick={() => this.selectAllHandler(true, NODE_TYPE.WALL)}>
                                                    Select all
                                                </div>
                                                <div
                                                    className="link"
                                                    onClick={() => this.selectAllHandler(false, NODE_TYPE.WALL)}>
                                                    Clear selection
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <div className="nodeCards">{wallCards}</div>
                                </>
                            )}
                            {this.state.systemMode !== SYSTEM_MODES.ON_PREM && !isCloudOnly && (
                                <>
                                    <div className="dcsHeaderContainer">
                                        <div className="dcsHeader">AD Connect ({adcCards ? adcCards.length : 0})</div>
                                    </div>
                                    <div className="dcCards">{adcCards}</div>
                                </>
                            )}
                            {!isCloudOnly && (
                                <>
                                    <div className="dcsHeaderContainer">
                                        <div className="dcsHeader">DCs ({dcCards ? dcCards.length : 0})</div>
                                        {this.state.statusUpdated && (
                                            <div className="link" onClick={() => this.changeShowUnprotectedState()}>
                                                {this.state.showUnprotected ? 'Hide Unprotected' : 'Show Unprotected'}
                                            </div>
                                        )}
                                    </div>
                                    <div className="dcCards">{dcCards}</div>
                                </>
                            )}

                            {this.state.showUpdateTowerConfirmationMessage && (
                                <ConfirmationMessage
                                    title="Before You Start"
                                    body={
                                        <div className="confirmationMessageContent">
                                            {`Make sure you create a snapshot of your Admin Console before you start the upgrade`}
                                            {/* TODO: Replace warning box with generic "label" component in version 5.0 */}
                                            <div className="warningBox">
                                                <img
                                                    src={promptWarningIcon}
                                                    alt="warning icon"
                                                    style={{ height: '32px' }}></img>
                                                <div>
                                                    <strong>Warning: </strong>
                                                    {`Failure to create a backup snapshot of your Admin Console before upgrade could result in data loss`}
                                                </div>
                                            </div>
                                            {`During the upgrade, Silverfort cannot send Notifications via email.`}
                                        </div>
                                    }
                                    approveText="Got it"
                                    OnCancel={this.cancelConfirmationHandler}
                                    OnApprove={this.updateTowerHandler}
                                />
                            )}
                            {this.state.showUpdateNodesConfirmationMessage && (
                                <ConfirmationMessage
                                    title="Upgrade Silverfort Nodes"
                                    body={
                                        <div className="confirmationMessageContent">
                                            {`Are you sure you want to upgrade the selected Silverfort nodes?`}
                                            <br />
                                            Note that notification emails will not be sent during the upgrade process.
                                        </div>
                                    }
                                    approveText="Upgrade"
                                    OnCancel={this.cancelConfirmationHandler}
                                    OnApprove={this.updateNodesHandler}
                                />
                            )}
                            {this.state.showUpdateDcsConfirmationMessage && (
                                <ConfirmationMessage
                                    title="Upgrade DCs"
                                    body={
                                        <div className="confirmationMessageContent">
                                            {`Are you sure you want to upgrade the selected DCs?`}
                                        </div>
                                    }
                                    approveText="Upgrade"
                                    OnCancel={this.cancelConfirmationHandler}
                                    OnApprove={this.updateDcsHandler}
                                />
                            )}
                            {this.state.showUpdatePushServicesConfirmationMessage && (
                                <ConfirmationMessage
                                    title="Upgrade Desktop Messaging Services"
                                    body={
                                        <div className="confirmationMessageContent">
                                            {`Are you sure you want to upgrade the selected Desktop Messaging Services?`}
                                            <br />
                                            Note that notification emails will not be sent during the upgrade process.
                                        </div>
                                    }
                                    approveText="Upgrade"
                                    OnCancel={this.cancelConfirmationHandler}
                                    OnApprove={this.updatePushServiceHandler}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default SoftwareUpdates;
